import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import { t } from "i18next";
import Deck from "../../shared/api/DeckApi";
import Lobby from "../../shared/api/LobbyApi";
import { publicWebUrl } from "../../shared/runtime";
import useGameContext from "../../shared/useGameContext";

interface ConfigData {
  code: string;
  configName: string;
  creatorName: string;
  deckIds: string[];
  deckInfos: any[];
  viewCount: number;
  createdAt: string;
}

export const ConfigPreviewPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const history = useHistory();
  const setLobbyKey = useGameContext().setLobbyKey;

  const [config, setConfig] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [launchingLobby, setLaunchingLobby] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await Deck.getDeckConfigurationByCode(code);
        if (!data) {
          setError(t("config_preview_not_found"));
          setLoading(false);
          return;
        }
        setConfig(data);

        // Update OG meta tags for social sharing
        updateMetaTags(data);
      } catch (e) {
        setError(t("config_preview_load_failed"));
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [code]);

  const updateMetaTags = (configData: ConfigData) => {
    const deckList = configData.deckInfos
      .slice(0, 3)
      .map((d) => d.name)
      .join(", ");
    const moreCount = configData.deckIds.length - 3;
    const deckDescription =
      moreCount > 0 ? `${deckList}, and ${moreCount} more` : deckList;

    const title = `${configData.configName} - rank.io`;
    const description = `${configData.creatorName} wants you to play a special rank.io version with you. Be ready for ${deckDescription}.`;

    // Update or create meta tags
    const updateOrCreateMeta = (name: string, content: string) => {
      let tag = document.querySelector(`meta[${name.includes("og:") ? "property" : "name"}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(name.includes("og:") ? "property" : "name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    updateOrCreateMeta("og:title", title);
    updateOrCreateMeta("og:description", description);
    updateOrCreateMeta("og:type", "website");
    updateOrCreateMeta("og:url", publicWebUrl(window.location.pathname));
    updateOrCreateMeta("twitter:title", title);
    updateOrCreateMeta("twitter:description", description);
    updateOrCreateMeta("twitter:card", "summary_large_image");

    // Update page title
    document.title = title;
  };

  const handlePlayWithThesDecks = async () => {
    if (!config) return;

    setLaunchingLobby(true);
    try {
      // Create new lobby
      const newLobby = await Lobby.newLobby();
      if (!newLobby || !newLobby.lobbyKey) {
        setError(t("config_preview_lobby_failed"));
        return;
      }

      // Store selected deck IDs in session storage
      // Lobby component will pick these up on mount
      sessionStorage.setItem("preSelectedDeckIds", JSON.stringify(config.deckIds));

      // Navigate to the new lobby — use setLobbyKey + /lobby (same as Start.tsx)
      // so we bypass the canJoin check in JoinPage that fails for a brand-new lobby
      setLobbyKey(newLobby.lobbyKey);
      history.push("/lobby");
    } catch (e) {
      setError(t("config_preview_game_failed"));
    } finally {
      setLaunchingLobby(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" style={{ textAlign: "center", marginTop: "60px" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !config) {
    return (
      <Container maxWidth="sm" style={{ marginTop: "60px" }}>
        <Card>
          <CardContent>
            <Typography color="error">
              {error || t("config_preview_not_found")}
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Get top 3 decks for preview
  const topDecks = config.deckInfos.slice(0, 3);
  const deckNames = topDecks.map((d) => d.name).join(", ");
  const moreCount = config.deckIds.length - 3;

  return (
    <Container maxWidth="sm" style={{ marginTop: "40px", marginBottom: "40px" }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {config.configName}
          </Typography>

          {/* Social-friendly messaging */}
          <Typography variant="body1" color="textSecondary" paragraph>
            {config.creatorName} {t("config_preview_invite_text")}{" "}
            {deckNames}
            {moreCount > 0 && `, ${t("config_preview_and_more", { count: moreCount })}`}.
          </Typography>

          {/* View count (optional) */}
          <Typography variant="caption" color="textSecondary">
            {config.viewCount} {t("config_preview_play_count")}
          </Typography>

          {/* Deck previews */}
          <Box marginTop={3} marginBottom={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t("config_preview_decks_included")}
            </Typography>
            {topDecks.map((deck) => (
              <Typography key={deck.id} variant="body2">
                • {deck.name}
              </Typography>
            ))}
            {moreCount > 0 && (
              <Typography variant="body2">
                • {t("config_preview_and_more", { count: moreCount })}
              </Typography>
            )}
          </Box>

          {/* CTA Button */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={handlePlayWithThesDecks}
            disabled={launchingLobby}
          >
            {launchingLobby ? t("config_preview_creating") : t("config_preview_play")}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ConfigPreviewPage;
